import { SIMULATED_TRANSCRIPT, SIMULATED_WORD_MS } from './constants';

/**
 * The slice of the Web Speech API this app uses. Declared structurally because
 * TypeScript's DOM library does not ship these types.
 */
interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((event: SpeechResultEventLike) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}

interface SpeechResultEventLike {
  results: ArrayLike<ArrayLike<{ transcript: string }>>;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

export interface DictationHandlers {
  /** `simulated` is true when the platform recogniser is unavailable. */
  onStart(simulated: boolean): void;
  onPartial(transcript: string): void;
  /** Fires once with the final transcript ('' if nothing was heard). */
  onDone(transcript: string): void;
}

export interface DictationSession {
  stop(): void;
}

function getRecogniser(): SpeechRecognitionCtor | null {
  const scope = globalThis as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return scope.SpeechRecognition ?? scope.webkitSpeechRecognition ?? null;
}

/**
 * Start spoken-English input. Uses the platform recogniser at `en-AU`, streaming
 * interim results. If the API is missing or errors — common in embedded
 * previews — it degrades to a clearly labelled simulated dictation so the flow
 * is still demonstrable, never passing simulated text off as heard speech.
 */
export function startDictation(handlers: DictationHandlers): DictationSession {
  let stopped = false;
  let recogniser: SpeechRecognitionLike | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;

  const clearTimer = (): void => {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };

  const detach = (): void => {
    if (!recogniser) return;
    recogniser.onresult = null;
    recogniser.onerror = null;
    recogniser.onend = null;
    try {
      recogniser.stop();
    } catch {
      /* already stopped */
    }
    recogniser = null;
  };

  const simulate = (): void => {
    if (stopped) return;
    const words = SIMULATED_TRANSCRIPT.split(' ');
    let spoken = 0;

    handlers.onStart(true);
    handlers.onPartial('');
    clearTimer();
    timer = setInterval(() => {
      spoken += 1;
      if (spoken > words.length) {
        clearTimer();
        handlers.onDone(words.join(' '));
        return;
      }
      handlers.onPartial(words.slice(0, spoken).join(' '));
    }, SIMULATED_WORD_MS);
  };

  const Recogniser = getRecogniser();
  if (!Recogniser) {
    simulate();
    return { stop: teardown };
  }

  try {
    const active = new Recogniser();
    let transcript = '';

    active.lang = 'en-AU';
    active.interimResults = true;
    active.continuous = false;

    active.onresult = (event) => {
      let text = '';
      for (let index = 0; index < event.results.length; index += 1) {
        text += event.results[index]?.[0]?.transcript ?? '';
      }
      transcript = text;
      handlers.onPartial(text);
    };
    active.onerror = () => {
      detach();
      simulate();
    };
    active.onend = () => {
      recogniser = null;
      if (stopped) return;
      handlers.onDone(transcript.trim());
    };

    active.start();
    recogniser = active;
    handlers.onStart(false);
  } catch {
    detach();
    simulate();
  }

  return { stop: teardown };

  function teardown(): void {
    stopped = true;
    clearTimer();
    detach();
  }
}
